import type { OwnerWorkspaceAsset } from 'config/api/assetRopa/assetRopaWorkspace.types';
import type { AssetProcessingActivityReviewRow, AssetRopaReview } from 'config/api/ropaReviews/ropaReviews.types';

export const mergeCurrentProcessingActivities = (
  current: AssetProcessingActivityReviewRow[],
  reviews: AssetProcessingActivityReviewRow[],
  active: boolean,
  hasActiveRound: boolean,
): AssetProcessingActivityReviewRow[] => {
  const byApaId = new Map(reviews.map(row => [row.assetProcessingActivityId, row]));
  return current.map(row => {
    const review = byApaId.get(row.assetProcessingActivityId);
    return {
      ...row,
      reviewId: review?.id ?? null,
      reviewStatus: review?.reviewStatus ?? (hasActiveRound ? 'not_in_review' : 'not_requested'),
      submittedRevision: review?.submittedRevision,
      isHistoricalReview: Boolean(review && !active),
      changedSinceReview: Boolean(review?.submittedRevision != null && row.revision != null && review.submittedRevision !== row.revision),
      canEdit: active && review ? review.canEdit ?? row.canEdit : row.canEdit,
      canAccept: Boolean(active && review?.canAccept),
      canRequestClarification: Boolean(active && review?.canRequestClarification),
    };
  });
};

const resolveMenuActions = (asset: OwnerWorkspaceAsset): Record<string, boolean> => {
  if (!asset.reviewOverlay || !asset.includedInCurrentReview) {
    return {};
  }

  const rows = asset.reviewOverlay.processingActivityReviews;
  const hasSubmitted = rows.some(row => row.reviewStatus === 'submitted' || row.canAccept);
  const canClarify = rows.some(row => row.canRequestClarification || row.reviewStatus === 'submitted');
  const hasIncomplete = rows.some(row => row.reviewStatus !== 'accepted' && row.reviewStatus !== 'cancelled');

  return {
    ...asset.menuActions,
    accept_all_submitted: asset.menuActions.accept_all_submitted ?? hasSubmitted,
    request_clarification: asset.menuActions.request_clarification ?? canClarify,
    cancel_incomplete_asset_review: asset.menuActions.cancel_incomplete_asset_review ?? hasIncomplete,
  };
};

export const mapOrganizationWorkspaceAssetToReview = (asset: OwnerWorkspaceAsset, hasActiveRound = false): AssetRopaReview => {
  const overlay = asset.reviewOverlay;

  return {
    id: asset.assetRopaId,
    assetRopaId: asset.assetRopaId,
    assetId: asset.assetId,
    assetName: asset.assetName,
    assetTypeId: asset.assetTypeId,
    assetTypeName: asset.assetTypeName,
    formalOwnerId: asset.formalOwnerId,
    formalOwnerName: asset.formalOwnerName,
    operationalOwnerMembershipId: asset.operationalOwnerMembershipId,
    operationalOwnerName: asset.operationalOwnerName,
    processingActivitiesCount: asset.processingActivitiesCount,
    permanentRopaStatus: overlay?.permanentRopaStatus ?? asset.permanentRopaStatus,
    reviewStatus: overlay?.reviewStatus ?? 'awaiting_response',
    warnings: {
      ...asset.warnings,
      dpiaIndicated: asset.processingActivityReviews.some(
        row => row.dpiaDisplayStatus === 'indicated' || row.dpiaRequestStatus === 'draft' || row.dpiaRequestStatus === 'indicated',
      ),
      dpiaRequiredUnapproved: asset.processingActivityReviews.some(row =>
        ['dpia_required', 'draft', 'in_progress', 'ready_for_review', 'clarification_requested'].includes(row.dpiaDisplayStatus),
      ),
    },
    processingActivityReviews: mergeCurrentProcessingActivities(
      asset.processingActivityReviews,
      overlay?.processingActivityReviews ?? [],
      asset.includedInCurrentReview,
      hasActiveRound,
    ),
    menuActions: resolveMenuActions(asset),
    gdprRelevanceStatus: asset.gdprRelevanceStatus,
    informationalCompleteness: asset.informationalCompleteness,
    lastUpdatedAt: asset.lastUpdatedAt,
    lastUpdatedByName: asset.lastUpdatedByName,
    includedInCurrentReview: asset.includedInCurrentReview,
    hasReviewOverlay: Boolean(overlay),
    assetRopaReviewId: overlay?.assetRopaReviewId ?? null,
  };
};

export const resolveAssetRopaReviewId = (asset: AssetRopaReview): number | null => asset.assetRopaReviewId ?? null;
